/* eslint-disable no-empty */

import { forwardRef, useState } from 'react';
import { Field } from 'formik';
import {
    FormControl,
    Stack,
    FormLabel,
    Box,
    Text,
    Spinner,
} from '@chakra-ui/react';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import walkthroughIds from '../../walkthroughIds';
import {
    stackSpacing,
    formLabelStyling,
    stackStyling,
    dropDownFormStyle,
    descriptionStyling,
} from 'components/forms/new/formStyles';
import { Select } from 'chakra-react-select';

const PlanWholesalerField = ({
    errors,
    touched,
    values,
    darkMode,
    loadingWholesalers,
    wholesalerOptions,
    setSelectedWholesalerID,
    setSelectedPartnerID,
    setPartnerOptions,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const ChakraSelect = forwardRef((props, ref) => (
        <Select selectedOptionColor="brand" ref={ref} {...props} />
    ));
    ChakraSelect.displayName = 'ChakraSelect';

    // Convert wholesalerOptions to react-select format
    const options = wholesalerOptions.map((opt) => ({
        value: opt.id,
        label: opt.name,
    }));
    const selectedOption = values?.wholesalerID
        ? options.find((op) => op.value === values.wholesalerID) || null
        : null;

    return (
        <FormControl
            isInvalid={!!errors.wholesalerID && touched.wholesalerID}
            sx={{ margin: '0 !important' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <Stack
                direction="row"
                spacing={stackSpacing}
                sx={stackStyling()}
                align="center">
                <Box>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Wholesaler
                    </FormLabel>
                    <Text sx={descriptionStyling} maxWidth={250}>
                        Select the wholesaler for this plan.
                    </Text>
                </Box>
                <Box flex="1" sx={{ paddingTop: '15px' }}>
                    {loadingWholesalers ? (
                        <Spinner size="sm" />
                    ) : (
                        <Field name="wholesalerID">
                            {({ form }) => (
                                <ChakraSelect
                                    minWidth="410px"
                                    width="100%"
                                    data-walkthroughid={
                                        walkthroughIds.plans.ufWholesalerSelect
                                    }
                                    options={options}
                                    placeholder="- Select Wholesaler -"
                                    menuPortalTarget={
                                        typeof window !== 'undefined'
                                            ? document.body
                                            : undefined
                                    }
                                    styles={{
                                        menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                        }),
                                    }}
                                    isClearable
                                    getOptionLabel={(i) => i.label}
                                    getOptionValue={(i) => i.value}
                                    chakraStyles={dropDownFormStyle(darkMode)}
                                    isLoading={form.isSubmitting}
                                    isDisabled={form.isSubmitting}
                                    value={selectedOption}
                                    onChange={(option) => {
                                        const wholesalerID = option
                                            ? option.value
                                            : '';
                                        form.setFieldValue(
                                            'wholesalerID',
                                            wholesalerID,
                                        );
                                        form.setFieldValue('partnerID', '');

                                        // Update selected wholesaler to trigger partner and rate list queries to refetch
                                        setSelectedWholesalerID(wholesalerID);
                                        setSelectedPartnerID('');

                                        // Clear partner options when wholesaler is cleared
                                        if (!wholesalerID) {
                                            setPartnerOptions([]);
                                        }

                                        form.validateField('wholesalerID');
                                    }}
                                    onBlur={() => {
                                        form.setFieldTouched(
                                            'wholesalerID',
                                            true,
                                        );
                                    }}
                                />
                            )}
                        </Field>
                    )}
                </Box>
                {isHovered && (
                    <FieldToolTip
                        label="Select the wholesaler for this plan."
                        ariaLabel="plan-wholesaler-tip"
                        left="25px"
                        top="5px"
                    />
                )}
            </Stack>
        </FormControl>
    );
};

export default PlanWholesalerField;
